import { useEffect, useState } from 'react';

import FormTitle from 'components/PageLayout/FormTitle';
import { WithBackButton } from 'components/curo-components/common/BackButton';
import CheckboxGroup from 'components/curo-components/common/CheckboxGroup';
import CheckboxWrapper from 'components/curo-components/common/CheckboxWrapper/CheckboxWrapper';
import ButtonWithLoadingState from 'components/digifi-wrappers/ButtonWithLoadingState';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { CustomIcon, WithAutoPayIcon } from 'static/images';
import { saveAutoPayOption } from 'thunks';
import styled from 'types/AppTheme';

import styles from './SetupAutoPayFillingOptionsStep.module.scss';

const PageDescriptionWrapper = styled.div`
  max-width: 690px;
  margin-bottom: 36px;
`;

const PAGE_TITLE = 'Choose how to pay';
const PAGE_DESCRIPTION = 'Please select the way you would like to pay off your loan.';

enum AutoPayMethod {
  WithAutoPay = 'WithAutoPay',
  WithoutAutoPay = 'WithoutAutoPay',
}

interface ISetupAutoPayFillingOptionsStepProps {
  onBackButtonClick: () => void;
  onContinueClick: () => void;
  withBackButton?: boolean;
}

const SetupAutoPayFillingOptionsStep = ({
  onBackButtonClick,
  onContinueClick,
  withBackButton = false,
}: ISetupAutoPayFillingOptionsStepProps) => {
  const [selectedMethod, setSelectedMethod] = useState<AutoPayMethod | null>(null);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  useEffect(() => {
    trackEvent(GoogleAnalyticsEventType.ApplyProcessAutoPayEnroll);
  }, []);

  const handleContinueClick = async () => {
    if (selectedMethod === AutoPayMethod.WithAutoPay) {
      onContinueClick();
      return;
    }

    await dispatchWithUnwrap(saveAutoPayOption({ autoPay: false }));
    trackEvent(GoogleAnalyticsEventType.AutopayDecision);
  };

  const renderButtons = () => {
    if (withBackButton && onBackButtonClick) {
      return (
        <WithBackButton onBackButtonClick={onBackButtonClick}>
          <ButtonWithLoadingState
            type="submit"
            kind="primary"
            size="form"
            onClick={handleContinueClick}
            disabled={!selectedMethod}
          >
            Next Steps
          </ButtonWithLoadingState>
        </WithBackButton>
      );
    }

    return (
      <ButtonWithLoadingState
        type="submit"
        kind="primary"
        size="form"
        onClick={handleContinueClick}
        disabled={!selectedMethod}
      >
        Next Steps
      </ButtonWithLoadingState>
    );
  };

  return (
    <>
      <FormTitle
        title={PAGE_TITLE}
        subTitle={<PageDescriptionWrapper>{PAGE_DESCRIPTION}</PageDescriptionWrapper>}
      />
      <CheckboxGroup>
        <CheckboxWrapper
          checked={selectedMethod === AutoPayMethod.WithAutoPay}
          value={AutoPayMethod.WithAutoPay}
          onChange={(value) => setSelectedMethod(value as AutoPayMethod)}
          title="Set up AutoPay"
          questionIconTooltip={
            <p className={styles.tooltip}>
              By setting up AutoPay, your payments will be automatically deducted from your bank
              account on your payment date.
            </p>
          }
        >
          <div className={styles.checkboxContent}>
            <WithAutoPayIcon />
            <span>Easiest</span>
          </div>
        </CheckboxWrapper>
        <CheckboxWrapper
          checked={selectedMethod === AutoPayMethod.WithoutAutoPay}
          value={AutoPayMethod.WithoutAutoPay}
          onChange={(value) => setSelectedMethod(value as AutoPayMethod)}
          title="Manual"
        >
          <div className={styles.checkboxContent}>
            <CustomIcon />
            <span>Pay manually each month</span>
          </div>
        </CheckboxWrapper>
      </CheckboxGroup>
      {renderButtons()}
    </>
  );
};

export default SetupAutoPayFillingOptionsStep;
